import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./authPage.css";
import logo from "../assets/images/logo.png";
import auth from "../assets/images/auth.png";
import { useAuth } from "../context/AuthContext";
import { useMutation } from "@apollo/client";
import {
  LOGIN_MUTATION,
  REGISTER_MUTATION,
} from "../graphql/userMutation";
import Loading from "../components/Loading";
import AlertMessage from "../components/AlertMessage";

const env = process.env.REACT_APP_ENV_SETTINGS || "dev";

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [username, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("male");

  const [referred_by, setreferred_by] = useState("");

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] =
    useState<"success" | "error">("success");

  const navigate = useNavigate();

  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [registerMutation] = useMutation(REGISTER_MUTATION);

  const { login } = useAuth();

  // ============================================================
  // REFERRAL URL
  // ============================================================

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const referralUserId = params.get("ref");

    if (referralUserId) {
      // Put referral user ID into Referred By
      setreferred_by(referralUserId);

      // Automatically switch to Sign Up
      setIsLogin(false);
    }
  }, []);

  // ============================================================
  // TOGGLE LOGIN / SIGNUP
  // ============================================================

  const toggleForm = () => {
    setIsLogin((prev) => !prev);

    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFullName("");
    setPhoneNumber("");
    setGender("");

    // Do not clear referral when switching form
    // if referral exists in URL.

    const params = new URLSearchParams(window.location.search);
    const referralUserId = params.get("ref");

    if (referralUserId) {
      setreferred_by(referralUserId);
    } else {
      setreferred_by("");
    }
  };

  // ============================================================
  // PASSWORD VISIBILITY
  // ============================================================

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  // ============================================================
  // ROLE REDIRECT
  // ============================================================

  const redirectBasedOnRole = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        navigate("/admin/dashboard");
        break;

      case "vendor":
        navigate("/vendor/dashboard");
        break;

      default:
        navigate("/");
    }
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setAlertMessage(null);

    try {
      // ========================================================
      // LOGIN
      // ========================================================

      if (isLogin) {
        if (env === "dev") {
          const res = await fetch("http://localhost:5000/login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email,
              password,
            }),
          });

          const data = await res.json();

          if (res.ok) {
            login(
              data.user,
              data.token,
              data.refreshToken
            );

            redirectBasedOnRole(data.user.role);
          } else {
            throw new Error(
              data.message || "Login failed"
            );
          }

        } else {

          const { data } = await loginMutation({
            variables: {
              input: {
                email,
                password,
              },
            },
          });

          login(
            data.login.user,
            data.login.token,
            data.login.refreshToken
          );

          redirectBasedOnRole(
            data.login.user.role
          );
        }

      // ========================================================
      // SIGN UP
      // ========================================================

      } else {

        if (password !== confirmPassword) {
          throw new Error(
            "Passwords do not match"
          );
        }

        // ======================================================
        // REFERRAL
        // ======================================================

        const params = new URLSearchParams(
          window.location.search
        );

        const referralUserId =
          params.get("ref");

        // Use URL referral first.
        // Otherwise use manually entered value.
        const finalReferredBy =
          referralUserId || referred_by;

        // ======================================================
        // REGISTER INPUT
        // ======================================================

        const input = {
          username,
          email,
          phoneNumber,
          password,
          gender,
          role: "user",
          status: "active",

          referred_by: finalReferredBy,
        };

        console.log(
          "Registration Input:",
          input
        );

        // ======================================================
        // DEV
        // ======================================================

        if (env === "dev") {

          const res = await fetch(
            "http://localhost:5000/users",
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body: JSON.stringify(input),
            }
          );

          const data = await res.json();

          if (res.ok) {

            login(
              data.user,
              data.token,
              data.refreshToken
            );

            redirectBasedOnRole(
              data.user.role
            );

          } else {

            throw new Error(
              data.message ||
                "Signup failed"
            );
          }

        // ======================================================
        // PRODUCTION
        // ======================================================

        } else {

          const { data } =
            await registerMutation({
              variables: {
                input,
              },
            });

          login(
            data.register.user,
            data.register.token,
            data.register.refreshToken
          );

          redirectBasedOnRole(
            data.register.user.role
          );
        }
      }

    } catch (error: any) {

      console.error(
        "Authentication error:",
        error
      );

      setAlertMessage(
        error.message ||
          "Unknown error"
      );

      setAlertType("error");

    } finally {

      setLoading(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return <Loading />;
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="auth-page">

      <div className="auth">

        {/* LOGO */}
        <div className="auth-logo-container">

          <img
            src={logo}
            alt="Lng Mart Logo"
            className="auth-logo"
          />

        </div>

        {/* ALERT */}
        {alertMessage && (
          <AlertMessage
            message={alertMessage}
            type={alertType}
          />
        )}

        <div className="auth-container">

          {/* IMAGE */}
          <img
            src={auth}
            alt="User authentication illustration"
            className="auth-image"
          />

          {/* TITLE */}
          <h2>
            {isLogin
              ? "Login"
              : "Sign Up"}
          </h2>

          <form
            onSubmit={handleSubmit}
            className="auth-form"
          >

            {/* ==================================================
                SIGNUP FIELDS
            ================================================== */}

            {!isLogin && (
              <>

                {/* USERNAME */}
                <div className="field">

                  <input
                    type="text"
                    id="username"
                    value={username}
                    placeholder=" "
                    required
                    onChange={(e) =>
                      setFullName(
                        e.target.value
                      )
                    }
                    aria-label="Full Name"
                  />

                  <label htmlFor="username">
                    User Name
                  </label>

                </div>

                {/* PHONE */}
                <div className="field">

                  <input
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    placeholder=" "
                    required
                    onChange={(e) =>
                      setPhoneNumber(
                        e.target.value
                      )
                    }
                    aria-label="Phone Number"
                  />

                  <label htmlFor="phoneNumber">
                    Phone Number
                  </label>

                </div>

                {/* REFERRED BY */}
                <div className="field">

                  <input
                    type="text"
                    id="referred_by"
                    value={referred_by}
                    placeholder=" "
                    onChange={(e) =>
                      setreferred_by(
                        e.target.value
                      )
                    }
                    aria-label="Referral Code"

                    // Prevent editing referral
                    // received from URL
                    readOnly={
                      !!new URLSearchParams(
                        window.location.search
                      ).get("ref")
                    }
                  />

                  <label htmlFor="referred_by">
                    Referred By
                  </label>

                </div>

              </>
            )}

            {/* ==================================================
                EMAIL
            ================================================== */}

            <div className="field">

              <input
                type="email"
                id="email"
                value={email}
                placeholder=" "
                required
                onChange={(e) =>
                  setEmail(
                    e.target.value
                  )
                }
                aria-label="Email"
              />

              <label htmlFor="email">
                Email
              </label>

            </div>

            {/* ==================================================
                PASSWORD
            ================================================== */}

            <div className="field password-field">

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                id="password"
                value={password}
                placeholder=" "
                required
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
                aria-label="Password"
              />

              <label htmlFor="password">
                Password
              </label>

              <span
                className="toggle-eye"
                onClick={
                  toggleShowPassword
                }
              >
                {showPassword
                  ? "🙈"
                  : "👁️"}
              </span>

            </div>

            {/* ==================================================
                CONFIRM PASSWORD
            ================================================== */}

            {!isLogin && (

              <div className="field">

                <input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  id="confirmPassword"
                  value={
                    confirmPassword
                  }
                  placeholder=" "
                  required
                  onChange={(e) =>
                    setConfirmPassword(
                      e.target.value
                    )
                  }
                  aria-label="Confirm Password"
                />

                <label htmlFor="confirmPassword">
                  Confirm Password
                </label>

              </div>

            )}

            {/* ==================================================
                ACTIONS
            ================================================== */}

            <div className="actions">

              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading
                  ? "Processing..."
                  : isLogin
                  ? "Login"
                  : "Sign Up"}
              </button>

              <p className="toggle-text">

                {isLogin
                  ? "Don't have an account?"
                  : "Already have an account?"}{" "}

                <span
                  className="toggle-link"
                  onClick={
                    toggleForm
                  }
                >
                  {isLogin
                    ? "Sign Up"
                    : "Login"}
                </span>

              </p>

            </div>

          </form>

        </div>

      </div>

    </div>
  );
};

export default AuthPage;
